import {z} from 'zod';
import {pluginConfigSchema, objectIdSchema} from '../shared';
import {timingConfigSchema} from './timing';
import {
  timelineActionClientSchema,
  timelineActionServerSchema,
  timelineActionTransforms,
} from './action';

export * from './action';
export * from './animation';
export * from './timing';
export * from './easing';
export * from './playback';

export type IX3TimelineVersionedResource = {
  id: TimelineId;
  version: number;
};

export const timelineIdSchema = z.string();
export type TimelineId = z.infer<typeof timelineIdSchema>;

/**
 * Upper bound for a persisted `canvasDuration` (seconds). The marker is nominally
 * 1s and never stored larger than the timeline UI's max supported duration (12s),
 * so cap it here: a crafted/corrupted payload with an extreme finite value (e.g.
 * 1e12) would otherwise pass validation and drive pathological timeline geometry
 * (timeline width, runtime pad timestamp).
 */
export const MAX_CANVAS_DURATION_S = 12;

/**
 * Schema for timeline reuse configuration.
 * Allows a timeline to reference another timeline's actions instead of defining its own.
 * When reuse is configured, operations on this timeline are redirected to the source
 * timeline's GSAP instance for true state synchronization.
 * Playback control (play, reverse, etc.) is handled via the standard Control dropdown.
 */
export const timelineReuseSchema = z.object({
  /** ID of the source timeline to reuse actions from */
  sourceTimelineId: timelineIdSchema,
});
export type TimelineReuse = z.infer<typeof timelineReuseSchema>;

/**
 * Schema for plugin-specific timeline metadata.
 * Used by multi-timeline triggers to identify the role of each timeline.
 *
 * Two surface areas, by design:
 *
 * - `role`, `distance`, `axes` are top-level because they describe a
 *   *shared* shape for multi-channel / continuous triggers: any future
 *   continuous trigger (mouse-move, scroll-with-axes, gamepad-axes, …)
 *   resolves the same channel-routing question (`role`) and may opt into
 *   the same distance-based interval cycling and per-axis enable bits.
 *   Validating them in core lets us bound persisted ranges (`distance`
 *   1..10000, `role` ≤ 64 chars) so a malicious payload can't write
 *   `Infinity`/`NaN`/oversized strings into Mongo Mixed.
 * - `pluginConfig` is a JSON-safe bag so each plugin owns the *rest* of
 *   its shape without core knowing the names of any plugin. Core stores
 *   it verbatim; plugins narrow at their boundary using their own zod
 *   schema or a runtime type guard.
 *
 * Mouse Move happens to be the first consumer of all three top-level
 * fields, but they are intentionally not nested inside `pluginConfig` —
 * doing so would re-export the same security/range concerns to every
 * future continuous trigger plugin.
 */
export const triggerMetadataSchema = z.object({
  /**
   * Semantic role within the trigger (e.g., 'mouseX', 'mouseY', 'open', 'close').
   * Used for routing timeline operations to the correct timeline in multi-timeline interactions.
   *
   * Kept as a permissive string so persisted timelines that predate (or
   * post-date) the canonical `TIMELINE_ROLE_NAMES` enum still parse. App
   * code that needs a `TimelineRoleName` should narrow at the boundary.
   */
  role: z.string().max(64).optional(),
  /**
   * Distance in pixels for interval action cycling on continuous triggers.
   * Generic across plugins — Mouse Move is the first consumer but any
   * continuous-input plugin can opt in. Bounded so a malicious payload
   * can't persist `Infinity`/`NaN`/extreme values into Mongo Mixed; the
   * runtime caps fires at `MAX_FIRES_PER_UPDATE` regardless.
   */
  distance: z.number().finite().int().min(1).max(10000).optional(),
  /**
   * Per-axis enable bits for this timeline group (velocity/interval).
   * Shared across continuous triggers: any plugin that drives 2D channels
   * can use the same axis-enable shape rather than re-implementing it
   * inside `pluginConfig`.
   */
  axes: z
    .object({x: z.boolean().optional(), y: z.boolean().optional()})
    .optional(),
  /**
   * Plugin-specific timeline config. A JSON-safe bag with an optional
   * `type` discriminator string (≤64 chars). Plugins set `type` and narrow
   * at their boundary; core stays ignorant of the shape but rejects
   * non-serializable or oversized payloads. Same name and validator as the
   * bag on `timelineAction` and (after the follow-up tightening) the
   * existing `triggerConfig.pluginConfig`.
   */
  pluginConfig: pluginConfigSchema.optional(),
});
export type TriggerMetadata = z.infer<typeof triggerMetadataSchema>;

/** Common fields shared between all timeline types */
const baseTimelineFields = {
  /** version used for realtime */
  version: z.number().int().nonnegative().optional(),
  /** Page ID that this timeline belongs to */
  pageId: objectIdSchema,
  /** Whether this timeline has been deleted */
  deleted: z.boolean().default(false),
  /** Optional human-friendly name for display in UI */
  name: z.string().optional(),
  /** Optional array of references to an action preset */
  actionPresetIds: z.array(z.string()).optional(),
  /** Preview playback speed multiplier */
  previewSpeed: z.number().positive().optional(),
  /** Whether this timeline should play in reverse */
  playInReverse: z.boolean().optional(),
  /** Whether this timeline should play immediately on load */
  immediate: z.boolean().optional(),
  /**
   * Nominal duration (in seconds) that the timeline's 0–100% canvas maps to.
   *
   * When present, the timeline uses a *fixed* percent canvas rather than one
   * derived from its content: the editor renders the ruler and the
   * duration/start inputs as percent (0–100%), and the runtime pads the GSAP
   * timeline to this length (see AnimationCoordinator) so that gesture progress
   * 0→1 spans the whole canvas — i.e. an action ending at 50% genuinely
   * completes at half the scroll/mouse travel, then holds.
   *
   * Only meaningful for gesture-driven (scroll-scrub / continuous) timelines.
   * Absent on legacy timelines, which keep content-derived behavior unchanged.
   */
  canvasDuration: z
    .number()
    .positive()
    .finite()
    .max(MAX_CANVAS_DURATION_S)
    .optional(),
  /** Timing configuration including animation timing and playback control settings */
  settings: timingConfigSchema.optional(),
  /**
   * User-managed group id for multi-timeline interactions. Bounded to a
   * non-empty, <=64-char string to mirror the trigger-side `assignedGroupId`
   * constraint (see interaction/trigger.ts): runtime/UI checks treat group ids
   * truthily, so an empty id would parse as valid yet behave as ungrouped, and
   * an unbounded id could be persisted/published in IX3 data.
   */
  groupId: z.string().min(1).max(64).optional(),
};

/** Timeline format used on the client (ie designer).
 * Action Targets are represented as tuples clientside. */
export const timelineClientSchema = z.object({
  id: timelineIdSchema,
  ...baseTimelineFields,
  /** Array of parallel actions to execute in this timeline */
  actions: z.array(timelineActionClientSchema),
  /**
   * Configuration for reusing actions from another timeline.
   * When set, this timeline acts as a reference and uses the source timeline's actions.
   */
  reuse: timelineReuseSchema.optional(),
  /**
   * Plugin-specific metadata for multi-timeline triggers.
   * Identifies the role of this timeline within a trigger (e.g., mouseX vs mouseY).
   */
  triggerMetadata: triggerMetadataSchema.optional(),
});

/** We define the frontend params here and create an explicit zod array to
 * help us parse/validate in the request. Each TimelineClient is the type of
 * each element in UpdateTimelineParams */
export type TimelineClient = z.infer<typeof timelineClientSchema>;
export type Timeline = TimelineClient;
export const clientTimelinesSchema = z.array(timelineClientSchema);
export type ClientTimelines = z.infer<typeof clientTimelinesSchema>;

/** Backend schema used for persistence (converted from TimelineClient).
 * Action Targets are stored as objects instead of tuples. */
export const timelineServerSchema = z.object({
  timelineId: timelineIdSchema,
  ...baseTimelineFields,
  /** Array of parallel actions to execute in this timeline */
  actions: z.array(timelineActionServerSchema),
  /**
   * Configuration for reusing actions from another timeline.
   * When set, this timeline acts as a reference and uses the source timeline's actions.
   */
  reuse: timelineReuseSchema.optional(),
  /**
   * Plugin-specific metadata for multi-timeline triggers.
   * Identifies the role of this timeline within a trigger (e.g., mouseX vs mouseY).
   */
  triggerMetadata: triggerMetadataSchema.optional(),
});

/** Persistence types for timeline updates.
 * UpdateTimelineDatum is a single element in the UpdateTimelineData array. */
export const timelineData = z.array(timelineServerSchema);
export type UpdateTimelineDatum = z.infer<typeof timelineServerSchema>;
export type UpdateTimelineData = z.infer<typeof timelineData>;

/** Represents the full timeline document in Mongo, including metadata.
 * This is returned to the client on read/write operations.
 * Uses intersection to add metadata fields to the discriminated union. */
const rawTimelineMetadata = z.object({
  createdOn: z.union([z.date(), z.null()]).optional(),
  updatedOn: z.union([z.date(), z.null()]).optional(),
  siteId: objectIdSchema,
});

export const rawTimelineSchema = timelineClientSchema.and(rawTimelineMetadata);
export type RawTimeline = z.infer<typeof rawTimelineSchema>;

/** Utility to convert timelines between client and server formats (tuple/object action targets). */
export const timelineTransforms = {
  clientToServer: ({
    id,
    actions,
    ...client
  }: TimelineClient): UpdateTimelineDatum => {
    return {
      timelineId: id,
      ...client,
      actions: actions.map(timelineActionTransforms.clientToServer),
    };
  },

  rawTimelineToClient: (rawTimeline: RawTimeline): TimelineClient => {
    return {
      id: rawTimeline.id,
      pageId: rawTimeline.pageId,
      actions: rawTimeline.actions,
      name: rawTimeline.name,
      actionPresetIds: rawTimeline.actionPresetIds,
      previewSpeed: rawTimeline.previewSpeed,
      playInReverse: rawTimeline.playInReverse,
      immediate: rawTimeline.immediate,
      canvasDuration: rawTimeline.canvasDuration,
      settings: rawTimeline.settings,
      deleted: rawTimeline.deleted,
      version: rawTimeline.version,
      reuse: rawTimeline.reuse,
      triggerMetadata: rawTimeline.triggerMetadata,
      groupId: rawTimeline.groupId,
    };
  },

  rawTimelineToVersionedResource: (
    rawTimeline: RawTimeline
  ): IX3TimelineVersionedResource => {
    return {
      id: rawTimeline.id,
      // Default to 0 (indicating no version exists in the db yet) if the version is missing
      version: rawTimeline.version || 0,
    };
  },

  /**
   * Converts a client timeline to a raw timeline object. Note: since the client
   * Timeline doesn't contain created/updated dates, we drop them from the Raw timeline as well. This is safer
   * and more accurate than creating placeholder Date objects with the current timestamp.
   */
  clientToRawTimeline: (
    clientTimeline: TimelineClient,
    siteId: string
  ): RawTimeline => {
    return {
      ...clientTimeline,
      siteId,
    };
  },
};

/** These types will assist us for publishing and scoping timelines per-page.*/
export type IX3TimelineMap = {
  [pageId: string]: TimelineClient[];
};

export type IX3TimelineServerMap = {
  [pageId: string]: UpdateTimelineDatum[];
};
